import {
  Component,
  ComponentInterface,
  Event,
  EventEmitter,
  Fragment,
  h,
  Prop,
  State,
} from '@stencil/core';

import searchEvents from '../paf-search-form/paf-search-form.events';
import resultsEvents from '../../components/paf-result-timetable-list/paf-result-timetable-list.events';
import rowEvents from '../../components/paf-result-timetable-row/paf-result-timetable-row.events';
import linkEvents from '../../components/paf-button/paf-button.events';
import { ApiRequestor } from '../../api-helpers/request';
import {
  AccessLeg,
  GetTripsByOriginAndDestinationRequest,
  PaginationCursor,
  PTRideLeg,
  Trip,
  TripResponse,
  TripsByOriginAndDestinationRequestBody,
} from '../../api/clients/journey-service';
import { InterfaceResultTimetableRow } from '../../components/paf-result-timetable-row/paf-result-timetable-row.custom';
import {
  addPriceToHiddenSummary,
  getCheapestPricePerTrip,
  pricePlaceHolder,
  splitTripsByDate,
  transformLevelOneData,
} from './data-transform/levelOne';
import {
  generateLegendDataForTrip,
  generateLevel2LegendFootnote,
  generateNotificationDataForLeg,
  getServiceCalendarPeriod,
  transformLevelTwoAlertData,
  transformLevelTwoFootpathData,
  transformLevelTwoLegData,
} from './data-transform/levelTwo';
import {
  defaultJourney,
  defaultJourneyId,
} from '../../components/paf-result-timetable-row/paf-result-timetable-row.sample-data';
import { InterfaceSearchForm } from '../paf-search-form/paf-search-form.custom';
import { InterfaceResultTimetableList } from '../../components/paf-result-timetable-list/paf-result-timetable-list.custom';
import * as searchFormHelper from '../paf-search-form/paf-search-form.helper';
import { TripPricesRequestPassengersEnum } from '../../api/clients/smapi-ticketing';

import mainConfig from './i18n/i18n';
import { InterfaceI18nConfig } from './i18n/i18n.interface';
import { Tracking } from '../../tracking/tracking';
import { formatDateForDatepicker } from '../../utils/date-helper';

@Component({
  shadow: true,
  styleUrl: 'paf-timetable.scss',
  tag: 'paf-timetable',
})

export class PafTimetable implements ComponentInterface {

  /**
   * The url of the Journey Service API. No slash at the end!
   * e.g. if the Endpoint is:
   * https://journey-service-int.api.sbb.ch/v3/trips/...
   *
   * then you should set this value to:
   * https://journey-service-int.api.sbb.ch
   */
  @Prop() public journeyServiceApiUrl!: string;

  /**
   * The url of the SMAPI. No slash at the end!
   * e.g. if the Endpoint is:
   * https://b2p.api.sbb.ch:443/api/v2/prices/...
   *
   * then you should set this value to:
   * https://b2p.api.sbb.ch:443
   */
  @Prop() public smapiUrl!: string;

  /**
   * The contract id to use as `x-contract-id` header for SMAPI requests.
   */
  @Prop() public smapiContractId!: string;

  /**
   * The language to use (for example for api request,for internal i18n values).
   * Acceptable values:
   * de, fr, it, en
   */
  @Prop() public language = 'de';

  /**
   * IMPORTANT: set this to false or omit the attribute in live-environment.
   * It's ment for local development only.
   */
  @Prop() public mock = false;

  /**
   * The initial value for the input. If set, the departure input from the
   * search form will be prefilled with this value.
   */
  @Prop() public departureValue?: string;

  /**
   * The initial value for the input. If set, the arrival input from the
   * search form will be prefilled with this value.
   */
  @Prop() public arrivalValue?: string;

  /**
   * If the search button should be a link, provide the href it here.
   */
  @Prop() public searchButtonLink?: string;

  /**
   * Property for the input fields.
   * Debounce type for the input change event in ms. If you set
   * this value to e.g. 300, we fire the input event only
   * every 300ms.
   */
  @Prop() public debounceInputEvent: 400;

  /**
   * Preset a date for the input field.
   */
  @Prop() public date?: string;

  /**
   * Preset a time for the input field.
   */
  @Prop() public time?: string;

  /**
   * If this property is set, no tracking data will be written to the
   * data-layer
   */
  @Prop() public disableTracking: boolean;

  @State() private _summary: InterfaceSearchForm['summary'];
  @State() private _isLoadingTrips = false;
  @State() private _isLoadingPrevNext = false;
  @State() private _config: InterfaceI18nConfig;
  @State() private _trips: InterfaceResultTimetableRow[] = undefined;
  @State() private _rawTripsData: TripResponse;
  @State() private _expandedRowId: string = undefined;
  @State() private _footnote: string = undefined;

  /**
   * Event which will be emitted if the focus should get
   * set on the summary title
   */
  /*
  @Event({
    bubbles: true,
    composed: true,
    eventName: 'paf-timetable_set-focus-on-summary',
  })
  public setFocusOnSummaryTitle: EventEmitter<void>;
  */

  /**
   * Event which will be emitted to cancel setting the focus
   * on the summary title
   */
  @Event({
    bubbles: true,
    composed: true,
    eventName: 'paf-timetable_cancel-focus-on-summary',
  })
  public cancelFocusOnSummaryTitle: EventEmitter<void>;

  private _tempTrips: InterfaceResultTimetableRow[] = undefined;
  private _apiRequestor: ApiRequestor;
  private _searchForm: HTMLPafSearchFormElement;
  private _resultsList: HTMLPafResultTimetableListElement;
  private _lastTripsRequest: TripsByOriginAndDestinationRequestBody;
  private _paginationCursor: PaginationCursor = {};
  private _rowToSetFocusAfterInsertion = 0;
  private _timeTableMutationObserver: MutationObserver;
  private _debounceTimer: ReturnType<typeof setTimeout>;
  private _tracking: Tracking;

  // ###############################################
  // Store and restore search in URL
  // ###############################################

  private _generateUrlWithSearchParams = (request: GetTripsByOriginAndDestinationRequest, baseUrl: string): URL => {
    // we need to set date to string, otherwise we can't serialize it to params
    const typelessRequest = request as any;
    const requestDate = typelessRequest.tripsByOriginAndDestinationRequestBody.date;

    request.tripsByOriginAndDestinationRequestBody.date = requestDate.toISOString();

    // create url with params
    const stringifiedRequest = searchFormHelper.stringifyConvertSetsToArray(request);

    const urlParams = new URLSearchParams(window.location.search);
    const url = new URL(baseUrl);

    urlParams.delete('search');
    urlParams.append('search', stringifiedRequest);
    url.search = urlParams.toString();

    return url;
  };

  private _storeRequestToUrlParams = (request: GetTripsByOriginAndDestinationRequest): void => {

    const url = this._generateUrlWithSearchParams(request, window.location.href);

    window.history.replaceState('', '', url);
  };

  private _restoreRequestFromUrlParams = (): GetTripsByOriginAndDestinationRequest => {
    const readParams = new URLSearchParams(window.location.search);

    // during parse, replace arrays with sets
    const typelessRequest = searchFormHelper.parseConvertArraysToSets(decodeURIComponent(readParams.get('search')));

    if (!typelessRequest) {
      return null;
    }

    // we need to create a date object from the date string again
    let requestDate = typelessRequest.tripsByOriginAndDestinationRequestBody.date;

    if (!requestDate) {
      requestDate = new Date();
    }

    const typedRequest = typelessRequest as GetTripsByOriginAndDestinationRequest;

    typedRequest.tripsByOriginAndDestinationRequestBody.date = new Date(requestDate);

    return typedRequest;
  };

  // ###############################################
  // Loading state
  // ###############################################

  private _handleStartLoadingTrips = (): void => {
    this._isLoadingTrips = true;

    // set pseudo text content on trips array
    this._trips = Array.from({
      length: 5,
    }, () => defaultJourney);

    // set pseudo text content on journey summary
    if (!this._isLoadingPrevNext) {
      this._summary = {
        assistiveTextDetails: 'Details',
        assistiveTextTitle: 'Title',
        changeButtonText: 'Suche ändern',
        date: '12.12.2024',
        departureArrival: 'Abfahrt',
        destination: 'Zürich',
        headingLevel: 2,
        origin: 'Bern',
        time: '10:00',
      };
    }
  };

  private _handleStartLoadingPreviousNextTrips = (prefOrNext: InterfaceResultTimetableList['previousOrNextConnections']): void => {
    if (prefOrNext === 'next') {
      this._trips.push(defaultJourney);
      this._rowToSetFocusAfterInsertion = this._tempTrips.length;
    } else {
      this._trips.unshift(defaultJourney);
      this._rowToSetFocusAfterInsertion = 0;
    }
  };

  private _handleFinishLoadingTrips = (): void => {
    this._isLoadingTrips = false;
    this._isLoadingPrevNext = false;

    // remove temp default journey (from prev/next loading trips)
    this._trips = this._tempTrips.filter((trip) => trip.id !== defaultJourneyId);
  };

  private _setFocusToInsertedTrips = (): void => {
    const currentTimeTableRows = this._getCurrentTimeTableRows();
    const rowToFocus = currentTimeTableRows[this._rowToSetFocusAfterInsertion].shadowRoot.querySelector('button');

    if (this._rowToSetFocusAfterInsertion > 0) {
      rowToFocus.scrollIntoView({
        behavior: 'instant',
        block: 'start',
      });
    }
    rowToFocus
      .focus({
        preventScroll: true,
      });
  };

  // ###############################################
  // Event handlers
  // ###############################################

  private _handleSummaryUpdate = (evt): void => {
    if (!this.searchButtonLink) {
      this._summary = evt.detail;
    }
  };

  // ###############################################
  // Prices
  // ###############################################

  private _addUrlParamsToBuyLink = (link: string): string => {
    const linkObject = new URL(link);
    const newParams = new URLSearchParams(linkObject.search);

    this._config.results.buyLinkUrlParams.forEach((param) => {
      newParams.append(param.key, param.value);
    });

    linkObject.search = newParams.toString();

    return linkObject.href;
  };

  private _generatePriceBadgeText = (price: number): string => {
    const configText = this._config.results.buy.priceLabel[this.language];

    return configText.replace('{{0}}', (price / 100).toFixed(2));
  };

  private _cleanUpPlaceholderPrices = (trips: InterfaceResultTimetableRow[]): InterfaceResultTimetableRow[] => {
    const tempTrips: InterfaceResultTimetableRow[] = JSON.parse(JSON.stringify(trips));

    tempTrips.forEach((trip) => {
      if (trip.priceBadge.priceText.indexOf(pricePlaceHolder) !== -1) {
        trip.priceBadge.priceText = '';
      }

      trip.summary = trip.summary.replace('{{10}}. ', '');
    });

    return tempTrips;
  };

  private _getPrices = async (): Promise<void> => {
    const tripsWithoutPrices: InterfaceResultTimetableRow[] = JSON.parse(JSON.stringify(this._tempTrips))
      .filter((trip) => trip.priceBadge.priceText.indexOf(pricePlaceHolder) !== -1);

    const tempTrips: InterfaceResultTimetableRow[] = JSON.parse(JSON.stringify(this._tempTrips));

    const tripIds = tripsWithoutPrices.map((trip) => trip.id);

    try {
      const prices = await this._apiRequestor.prices({
        tripPricesRequest: {
          passengers: [`paxa;42;${TripPricesRequestPassengersEnum.HalfFare}` as TripPricesRequestPassengersEnum],
          tripIds,
        },
        xContractId: this.smapiContractId,

        // Feedback from Lichtenberger Laura @SBB:
        // Die Conversation ID erstellst du selber und nutzt diese dann für
        // den gesamten Prozess

        // generated here: https://www.uuidgenerator.net/version4

        xConversationId: '2630ac35-b046-46e2-baaa-901c9dacce35',
      }, this.language);

      const pricesTransformed = getCheapestPricePerTrip(prices);

      tempTrips.forEach((trip) => {
        if (Object.keys(pricesTransformed)
          .includes(trip.id)) {
          const priceObject = pricesTransformed[trip.id];

          trip.priceBadge.priceText = this._generatePriceBadgeText(priceObject.price);
          trip.priceBadge.isDiscount = priceObject.supersaver;
          trip.link.href = this._addUrlParamsToBuyLink(priceObject.link);

          trip.summary = addPriceToHiddenSummary(
            trip.summary,
            this._config,
            (priceObject.price / 100).toFixed(2)
              .toString(),
            priceObject.supersaver,
            this.language,
          );

        }
      });

      // in the loading rows, we have a placeholder price XX.XX. If we did
      // not receive a price for a certain connection, clean it up.
      this._tempTrips = this._cleanUpPlaceholderPrices(tempTrips);

    } catch {
      this._tempTrips = this._cleanUpPlaceholderPrices(this._tempTrips);
    }
  };

  // ###############################################
  // Trips requests
  // ###############################################

  private _doRequestWithPayload = async (payload: GetTripsByOriginAndDestinationRequest, skipUrlStore: boolean): Promise<void> => {
    // check if we just did the same request before
    if (this._lastTripsRequest) {
      const stringifiedPayload = searchFormHelper.stringifyConvertSetsToArray(payload.tripsByOriginAndDestinationRequestBody);
      const stringifiedLastRequest = searchFormHelper.stringifyConvertSetsToArray(this._lastTripsRequest);

      if (stringifiedLastRequest === stringifiedPayload) {
        return;
      }
    }

    this._handleStartLoadingTrips();

    this._lastTripsRequest = payload.tripsByOriginAndDestinationRequestBody;

    try {
      this._rawTripsData = await this._apiRequestor.trips(payload, this.language);

      // console.log(this._rawTripsData);

      const tempResults = transformLevelOneData(this._rawTripsData, payload.tripsByOriginAndDestinationRequestBody.includeAccessibility !== 'NONE', this._config, this.language);

      this._tempTrips = tempResults.results;

      // save pagination cursors
      this._paginationCursor.next = tempResults.paginationCursor.next;
      this._paginationCursor.previous = tempResults.paginationCursor.previous;

      if (this._config.results.storeAndRestoreSearchInUrl && !skipUrlStore) {
        this._storeRequestToUrlParams(payload);
      }

      this._trackSearch();

    } catch {
      this._tempTrips = [];
      this._summary = undefined;
      this._handleFinishLoadingTrips();

      this._trackSearch();
    }
  };

  private _handleSearchFormSearchRequest = async (evt): Promise<void> => {
    const payload: GetTripsByOriginAndDestinationRequest = evt.detail;

    if (this.searchButtonLink) {
      evt.preventDefault();

      const url = this._generateUrlWithSearchParams(payload, this.searchButtonLink);

      window.location.href = url.toString();

      return;
    }

    await this._doRequestWithPayload(payload, false);
    await this._getPrices();
    await this._getFootnoteData();

    this._handleFinishLoadingTrips();

    this._expandedRowId = undefined;

    /*
    setTimeout(() => {
      this.setFocusOnSummaryTitle.emit();
    }, 300);
    */
  };

  private _handlePrevNextClicked = async (evt): Promise<void> => {
    const prefOrNext: InterfaceResultTimetableList['previousOrNextConnections'] = evt.detail;

    this._expandedRowId = undefined;
    this._isLoadingPrevNext = true;
    this._handleStartLoadingPreviousNextTrips(prefOrNext);

    const hasPaginationCursor = this._paginationCursor.next && this._paginationCursor.previous;

    if (!hasPaginationCursor || !this._lastTripsRequest) {
      return;
    }

    // save date as date object as again
    const dateObject = new Date(this._lastTripsRequest.date);

    this._lastTripsRequest.date = dateObject;

    // do request
    const oldRawTripsData = this._rawTripsData;

    try {
      const newRawTripsData = await this._apiRequestor.trips({
        tripsByOriginAndDestinationRequestBody: {
          ...this._lastTripsRequest,
          pagingCursor: prefOrNext === 'previous'
            ? this._paginationCursor?.previous
            : this._paginationCursor?.next,
        },
      }, this.language);

      // save new pagination cursors
      if (prefOrNext === 'next') {
        this._paginationCursor.next = newRawTripsData.paginationCursor.next;
      } else {
        this._paginationCursor.previous = newRawTripsData.paginationCursor.previous;
      }

      // transform
      const tempTrips = transformLevelOneData(newRawTripsData, this._lastTripsRequest.includeAccessibility !== 'NONE', this._config, this.language);

      const oldTrips = this._tempTrips;
      const newTrips = tempTrips.results;

      this._tempTrips = prefOrNext === 'next'
        ? oldTrips.concat(newTrips)
        : newTrips.concat(oldTrips);

      if (prefOrNext === 'next') {
        this._rawTripsData = {
          ...this._rawTripsData,
          trips: this._rawTripsData.trips.concat(newRawTripsData.trips),
        };
      } else {
        this._rawTripsData = {
          ...this._rawTripsData,
          trips: newRawTripsData.trips.concat(oldRawTripsData.trips),
        };
      }

      await this._getPrices();

      this._setupTimeTableMutationObserver();

      this._handleFinishLoadingTrips();

      this._trackPreviousNextClick(prefOrNext !== 'next');
    } catch {
      this._trips = [];
    }

  };

  private _getCurrentTimeTableRows = (): HTMLPafResultTimetableRowElement[] => {
    const lists = this._resultsList.querySelectorAll('ol');
    const allRowReferences: HTMLPafResultTimetableRowElement[] = [];

    // Loop through each list and collect items
    lists.forEach((list) => {
      const rows = Array.from(list.querySelectorAll('paf-result-timetable-row'));

      allRowReferences.push(...rows);
    });

    return allRowReferences;
  };

  // ###############################################
  // Journey Summary
  // ###############################################

  private _generateJourneySummary = async (request: GetTripsByOriginAndDestinationRequest): Promise<void> => {
    const body = request.tripsByOriginAndDestinationRequestBody;

    const destination = await searchFormHelper.getLocationNameFromDidok(body.destination, this._apiRequestor, this.language);
    const origin = await searchFormHelper.getLocationNameFromDidok(body.origin, this._apiRequestor, this.language);
    let via = '';

    if (body.vias && body.vias?.length > 0) {
      via = await searchFormHelper.getLocationNameFromDidok(body.vias[0].stopPlaceId, this._apiRequestor, this.language);
    }

    this._summary = searchFormHelper.getJourneySummaryData({
      config: this._config,
      date: body.date.toISOString(),
      destination,
      forArrival: body.forArrival,
      language: this.language,
      origin,
      time: body.time,
      via,
      viaDescription: this._config.inputForm.extendedSearch.via.label[this.language],
    });
  };

  // ###############################################
  // Leg Rendering
  // ###############################################

  private _renderFootpath = (leg: AccessLeg): HTMLPafResultFootpathElement => {
    const transformedLegData = transformLevelTwoFootpathData(leg, this._lastTripsRequest.includeAccessibility !== 'NONE', this._config, this.language);

    return (
      <paf-result-footpath
        description={transformedLegData.description}
        time={transformedLegData.time}
        timeAssistiveText={transformedLegData.timeAssistiveText}
        wheelchair={transformedLegData.wheelchair}
      />
    );
  };

  private _renderAlert = (leg: PTRideLeg, rawTrip: Trip, currentLegNumber: number): HTMLPafResultAlertElement[] => {
    const transformedLegData = transformLevelTwoAlertData(leg, rawTrip, currentLegNumber);

    if (!transformedLegData) {
      return null;
    }

    return (
      transformedLegData.map((item) => (
        <paf-result-alert
          description={item.description}
          iconName={item.iconName}
        />
      ))
    );
  };

  private _renderPtLeg = (leg: PTRideLeg, currentLegNumber: number, totalLegs: number, rawTrip: Trip): HTMLPafResultTimetableLegElement => {
    const transformedLegData = transformLevelTwoLegData(leg, this._lastTripsRequest.includeAccessibility !== 'NONE', this._config, currentLegNumber, totalLegs, this.language);

    return (
      <Fragment>
        {this._renderAlert(leg, rawTrip, currentLegNumber)}
        <paf-result-timetable-leg
          summaryArrival={transformedLegData.summary.details.arrival}
          summaryDeparture={transformedLegData.summary.details.departure}
          heading={transformedLegData.summary.heading.title}
          headingLevel={transformedLegData.summary.heading.level}
        >
          <div slot='departure-time'>
            <paf-result-time
              delay={transformedLegData.journey.departure.time.delay}
              time={transformedLegData.journey.departure.time.time}
            />
          </div>
          <div slot='arrival-time'>
            <paf-result-time
              delay={transformedLegData.journey.arrival.time.delay}
              time={transformedLegData.journey.arrival.time.time}
            />
          </div>
          <div slot='pearl-chain'>
            <paf-result-pearl-chain
              legs={transformedLegData.pearlChain.legs}
              now={transformedLegData.pearlChain.now}
              orientation={transformedLegData.pearlChain.orientation}
            />
          </div>
          <div slot='station-and-platform-departure'>
            <paf-result-station-and-platform
              platform={transformedLegData.journey.departure.platform}
              platformChange={transformedLegData.journey.departure.platformChange}
              stationName={transformedLegData.journey.departure.stationName}
            />
          </div>

          {transformedLegData.wheelChairInfo.departure?.description &&
            <div slot='wheelchair-info-departure'>
              <paf-result-legend-item
                description={transformedLegData.wheelChairInfo.departure.description}
                iconName={transformedLegData.wheelChairInfo.departure.iconName}
                iconVariant={transformedLegData.wheelChairInfo.departure.iconVariant}
                renderWithoutRole={true}
              />
            </div>
          }

          <div slot='service'>
            <paf-result-row-header
              direction={transformedLegData.service.direction}
              level={transformedLegData.service.level}
              picto={transformedLegData.service.picto}
              productIcon={transformedLegData.service.productIcon}
              productText={transformedLegData.service.productText}
            />
          </div>
          <div slot='attributes'>
            <paf-result-attributes
              config={transformedLegData.attributes.config}
              level={transformedLegData.attributes.level}
            />
          </div>
          <div slot='cus-him-inline'>
            {transformedLegData.inlineCusHim.messages.map((message) => (
              <paf-result-legend-item
                description={message.description}
                iconName={message.iconName}
                iconVariant='cus-him-l2'
                renderWithoutRole={true}
              />
            ))}
          </div>
          <div slot='utilization'>
            <paf-result-utilization
              config={transformedLegData.utilization.config}
              level={transformedLegData.utilization.level}
            />
          </div>
          <div slot='station-and-platform-arrival'>
            <paf-result-station-and-platform
              platform={transformedLegData.journey.arrival.platform}
              platformChange={transformedLegData.journey.arrival.platformChange}
              stationName={transformedLegData.journey.arrival.stationName}
            />
          </div>

          {transformedLegData.wheelChairInfo.arrival?.description &&
            <div slot='wheelchair-info-arrival'>
              <paf-result-legend-item
                description={transformedLegData.wheelChairInfo.arrival.description}
                iconName={transformedLegData.wheelChairInfo.arrival.iconName}
                iconVariant={transformedLegData.wheelChairInfo.arrival.iconVariant}
                renderWithoutRole={true}
              />
            </div>
          }
        </paf-result-timetable-leg>

        {/* NOTIFICATION */}
        {this._renderNotificationForLeg(leg)}
      </Fragment>
    );
  };

  private _renderLegsForTrip(trip: InterfaceResultTimetableRow): (HTMLPafResultTimetableLegElement | HTMLPafResultFootpathElement)[] {
    const renderedElements: (HTMLPafResultTimetableLegElement | HTMLPafResultFootpathElement)[] = [];

    if (!this._rawTripsData) {
      return null;
    }

    // get trip from api response with id from stored trips array
    const tripData = this._rawTripsData.trips.filter((rawTrip) => rawTrip.id === trip.id);

    if (!tripData || tripData.length < 1) {
      return null;
    }

    const amountOfRideLegs = tripData[0].legs.filter((leg) => leg.type === 'PTRideLeg').length;
    let rideLegCounter = 0;

    // iterate legs of trip
    tripData[0].legs.forEach((leg) => {
      if (leg.type === 'AccessLeg') {
        renderedElements.push(this._renderFootpath(leg as AccessLeg));
      }

      if (leg.type === 'PTRideLeg') {
        rideLegCounter++;
        renderedElements.push(this._renderPtLeg(leg as PTRideLeg, rideLegCounter, amountOfRideLegs, tripData[0]));
      }
    });

    return renderedElements;
  }

  private _renderNotificationForLeg = (leg: PTRideLeg): HTMLPafNotificationElement[] => {
    if (!leg) {
      return null;
    }

    const notificationData = generateNotificationDataForLeg(leg, this._config, this.language);

    if (!notificationData || notificationData?.content.length < 1) {
      return null;
    }

    return (
      <div class='notification'>
        <paf-notification
          titleText={notificationData.titleText}
          titleLevel={notificationData.titleLevel}
          content={notificationData.content}
        />
      </div>
    );
  };

  private _renderLegendForTrip = (id: string): HTMLPafResultLegendElement => {
    if (!this._rawTripsData) {
      return null;
    }

    // get trip from api response with id from stored trips array
    const tripData = this._rawTripsData.trips.filter((rawTrip) => rawTrip.id === id);

    if (!tripData || tripData.length < 1) {
      return null;
    }

    const legendData = generateLegendDataForTrip(tripData[0], this._config, this.language, this._lastTripsRequest.includeAccessibility !== 'NONE');

    // if (legendData.length < 1) {
    //   return null;
    // }

    return (
      <div
        class='legend'
      >
        <paf-result-legend
          firstFootnote={this._footnote}
          titleLevel={this._config.results.legend.titleLevel}
          titleText={this._config.results.legend.title[this.language]}
          data-testid='legend'
        >
          {legendData.map((legendItem) => (
            <paf-result-legend-item
              description={legendItem.description}
              iconName={legendItem.iconName}
              iconVariant={legendItem.iconVariant}
              renderWithoutRole={legendItem.renderWithoutRole}
            />
          ))}

        </paf-result-legend>
      </div>
    );
  };

  private _getFootnoteData = async (): Promise<void> => {
    if (this._footnote) {
      return;
    }

    const serviceCalendarResponse = await this._apiRequestor.timetablePeriod();
    const serviceCalendarPeriod = getServiceCalendarPeriod(serviceCalendarResponse);

    this._footnote = generateLevel2LegendFootnote(serviceCalendarPeriod, this._config, this.language);
  };

  private _renderTimetableRow = (trip: InterfaceResultTimetableRow): HTMLPafResultTimetableRowElement => {
    const showLoading = this._isLoadingTrips || (this._isLoadingPrevNext && trip.id === defaultJourneyId);
    const ariaAttributesLevel2 = {};

    if (trip.id !== this._expandedRowId) {
      ariaAttributesLevel2['aria-hidden'] = 'true';
      ariaAttributesLevel2['role'] = 'presentation';
      ariaAttributesLevel2['inert'] = true;
    }

    // don't render default journey row (from loading prev/next)
    if (!this._isLoadingPrevNext && trip.id === defaultJourneyId && !this._isLoadingTrips) {
      return null;
    }

    return (
      <paf-result-timetable-row
        expanded={this._expandedRowId === trip.id}
        heading={trip.heading}
        headingLevel={trip.headingLevel}
        summary={trip.summary}
        loading={showLoading}
        key={trip.id}
        id={trip.id}
      >

        {/* ################################################## */}
        {/* LEVEL 1 */}
        {/* ################################################## */}
        <div slot='row-header'>
          <paf-result-row-header
            picto={trip.rowHeader.picto}
            productIcon={trip.rowHeader.productIcon}
            productText={trip.rowHeader.productText}
            direction={trip.rowHeader.direction}
            loading={showLoading}
            level='1'
          />
        </div>
        <div slot='pearl-chain-time'>
          <paf-result-pearl-chain-time
            arrivalTime={trip.pearlChainTime.arrivalTime}
            arrivalWalk={trip.pearlChainTime.arrivalWalk}
            departureTime={trip.pearlChainTime.departureTime}
            departureWalk={trip.pearlChainTime.departureWalk}
            loading={showLoading}
            wheelchair={trip.pearlChainTime.wheelchair}
            slot='pearl-chain-time'
          >
            <paf-result-pearl-chain
              legs={trip.pearlChain.legs}
              now={trip.pearlChain.now}
              orientation='horizontal'
            ></paf-result-pearl-chain>
          </paf-result-pearl-chain-time>
        </div>
        <div slot='row-footer'>
          <paf-result-row-footer
            attributeIcons={trip.rowFooter.attributeIcons}
            cusHimIcons={trip.rowFooter.cusHimIcons}
            duration={trip.rowFooter.duration}
            hasPlatformChange={trip.rowFooter.hasPlatformChange}
            occupancyClassOne={trip.rowFooter.occupancyClassOne}
            occupancyClassTwo={trip.rowFooter.occupancyClassTwo}
            platform={trip.rowFooter.platform}
            loading={showLoading}
          />
        </div>
        <div slot='buy-button'>
          {trip.link.href &&
            <paf-button
              class='result-timetable-row__link'
              aLabelValue={trip.link.aLabelValue}
              disabled={trip.link.disabled}
              href={trip.link.href}
              isLink={true}
              target='_blank'
              text={trip.link.text}
              variant={trip.link.variant}
              data-testid='buy-button'
            />
          }
        </div>
        <div slot='price-badge'>
          {trip.priceBadge.priceText &&
            <paf-result-price-badge
              aria-hidden='true'
              isDiscount={trip.priceBadge.isDiscount}
              priceText={trip.priceBadge.priceText}
              loading={showLoading}
              role='presentation'
            />
          }
        </div>

        {/* ################################################## */}
        {/* LEVEL 2 */}
        {/* ################################################## */}
        <div
          class={{
            'is-expanded': trip.id === this._expandedRowId,
            'level-2': true,
          }}
          slot='level-2'
          {...ariaAttributesLevel2}
        >
          <div
            class='legs-wrapper'
            data-testid='level-2'
          >
            {trip.link.href &&
              <paf-button
                class='result-timetable-row__link-mobile'
                aLabelValue={trip.link.aLabelValue}
                disabled={(trip.id !== this._expandedRowId) || trip.link.disabled}
                href={trip.link.href}
                isLink={true}
                target='_blank'
                text={trip.link.text}
                variant={trip.link.variant}
                data-testid='buy-button-mobile'
              />
            }

            {this._renderLegsForTrip(trip)}

            {/* LEGEND */}
            {this._renderLegendForTrip(trip.id)}
          </div>

        </div>
      </paf-result-timetable-row>
    );
  };

  private _toggleTabIndexesOnLevel2 = (expandedElement: string, expanding: boolean): void => {
    const rows = this._resultsList.querySelectorAll('paf-result-timetable-row');
    let level2;

    rows.forEach((row) => {
      if (row.id === expandedElement) {
        level2 = (row as HTMLElement);
      }
    });

    const linksInNotifications = level2.querySelector('paf-notification')?.shadowRoot.querySelectorAll('a');
    const linksInLegend = level2.querySelector('paf-result-legend')?.shadowRoot.querySelectorAll('a');

    const linksInNotificationsArray = linksInNotifications
      ? Array.from(linksInNotifications)
      : [];
    const linksInLegendArray = linksInLegend
      ? Array.from(linksInLegend)
      : [];
    const allLinks = [
      ...linksInNotificationsArray,
      ...linksInLegendArray,
    ];

    allLinks.forEach((link) => {
      const linkElement = (link as HTMLElement);

      if (expanding) {
        linkElement.removeAttribute('tabindex');
      } else {
        linkElement.setAttribute('tabindex', '-1');
      }
    });
  };

  private _handleRowButtonChanged = (evt): void => {

    if (this._expandedRowId) {
      this._toggleTabIndexesOnLevel2(this._expandedRowId, false);
    }

    if (!evt.detail) {
      const closedRow = this._trips.find((trip) => trip.id === this._expandedRowId);

      this._trackExpandCollapseRow(closedRow, false);

      this._expandedRowId = undefined;

      return;
    }

    const eventPath = evt.composedPath();

    if (eventPath.length < 1) {
      return;
    }

    const [firstElem] = eventPath;

    if (firstElem.localName !== 'paf-result-timetable-row') {
      return;
    }

    this._expandedRowId = firstElem.id;
    this._toggleTabIndexesOnLevel2(this._expandedRowId, true);
    this.cancelFocusOnSummaryTitle.emit();

    const expandedRow = this._trips.find((trip) => trip.id === this._expandedRowId);

    this._trackExpandCollapseRow(expandedRow, true);

  };

  private _setupTimeTableMutationObserver = (): void => {
    this._timeTableMutationObserver = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          clearTimeout(this._debounceTimer);
          this._debounceTimer = setTimeout(() => {
            this._setFocusToInsertedTrips();
            this._timeTableMutationObserver.disconnect();
          }, 500);
        }
      }
    });

    this._timeTableMutationObserver.observe(this._resultsList, {
      attributes: false,
      childList: true,
      subtree: false,
    });
  };

  // ###############################################
  // TRACKING
  // ###############################################

  private _getTrackingTitleForRow = (row: InterfaceResultTimetableRow): string => {
    let title = '';

    if (row.rowHeader.productIcon) {
      title += `${row.rowHeader.productIcon} `;
    }

    if (row.rowHeader.productText) {
      title += `${row.rowHeader.productText} `;
    }

    if (row.rowHeader.direction) {
      title += row.rowHeader.direction;
    }

    return title;
  };

  private _getTrackingPriceForRow = (row: InterfaceResultTimetableRow): string => {
    let price = '';

    if (row.priceBadge.priceText) {
      const {
        priceText,
      } = row.priceBadge;

      price = priceText.split('CHF')[1].trim();
    }

    return price;
  };

  private _trackSearch = (): void => {
    const accessibility = this._lastTripsRequest.includeAccessibility && (this._lastTripsRequest.includeAccessibility !== 'NONE');
    const bicycle = this._lastTripsRequest.includeNoticeAttributes.has('BIKE_TRANSPORT');

    this._tracking.timetableSearch({
      accessibility,
      bicycle,
      destination: this._searchForm.trackingDestination,
      origin: this._searchForm.trackingOrigin,
      results: this._tempTrips.length,
      via: this._searchForm.trackingVia || undefined,
    });
  };

  private _trackExpandCollapseRow = (row: InterfaceResultTimetableRow, expanded: boolean): void => {
    this._tracking.openOrCollapseLevelTwo({
      changes: JSON.parse(row.pearlChain.legs).length - 1,
      duration: row.rowFooter.duration,
      opening: expanded,
      price: this._getTrackingPriceForRow(row),
      title: this._getTrackingTitleForRow(row),
    });
  };

  private _trackBuyButtonClick = (evt): void => {
    const eventPath = evt.composedPath();

    const buttons = eventPath.filter((item) => item?.nodeName?.toLowerCase() === 'paf-button');

    const rows = eventPath.filter((item) => item?.nodeName?.toLowerCase() === 'paf-result-timetable-row');

    if (buttons.length < 1 || rows.length < 1) {
      return;
    }

    const [button] = buttons;
    const [row] = rows;
    const rowsData = this._trips.filter((trip) => trip.id === row.id);

    if (rowsData.length < 1) {
      return;
    }

    const [rowData] = rowsData;

    this._tracking.buyButtonClick({
      changes: JSON.parse(rowData.pearlChain.legs).length - 1,
      duration: rowData.rowFooter.duration,
      link: button.href,
      price: this._getTrackingPriceForRow(rowData),
      title: this._getTrackingTitleForRow(rowData),
    });

  };

  private _trackPreviousNextClick = (isForPreviousConnections: boolean): void => {
    this._tracking.previousNextConnections({
      isForPreviousConnections,
    });
  };

  // ###############################################
  // Component lifecycle
  // ###############################################
  public componentWillLoad(): Promise<void> | void {
    const tempConfig: InterfaceI18nConfig = mainConfig;

    this._apiRequestor = new ApiRequestor(this.journeyServiceApiUrl, this.smapiUrl, this.mock);
    this._tracking = new Tracking(this.disableTracking);

    // overwrite config with property values

    if (this.departureValue) {
      tempConfig.inputForm.inputFrom.value = this.departureValue;
    }

    if (this.arrivalValue) {
      tempConfig.inputForm.inputTo.value = this.arrivalValue;
    }

    if (this.debounceInputEvent) {
      tempConfig.inputForm.autocomplete.debounceInputEvent = this.debounceInputEvent;
    }

    if (this.date) {
      tempConfig.inputForm.date.value = this.date;
    }

    if (this.time) {
      tempConfig.inputForm.time.value = this.time;
    }

    this._config = tempConfig;
  }

  public async componentDidLoad(): Promise<void> {
    this._searchForm.addEventListener(searchEvents.demandsTimetableRequest, this._handleSearchFormSearchRequest);
    this._searchForm.addEventListener(searchEvents.demandsJourneySummaryUpdate, this._handleSummaryUpdate);
    this._resultsList.addEventListener(resultsEvents.prevNextClicked, this._handlePrevNextClicked);
    this._resultsList.addEventListener(rowEvents.timeTableRowButtonChanged, this._handleRowButtonChanged);
    this._resultsList.addEventListener(linkEvents.clicked, this._trackBuyButtonClick);

    if (this._config.results.storeAndRestoreSearchInUrl) {
      const request = this._restoreRequestFromUrlParams();

      this._config = searchFormHelper.adjustAppConfigFromUrlParams(request, this._config);

      if (request) {
        await this._doRequestWithPayload(request, true);
        await this._generateJourneySummary(request);
        await this._getPrices();
        await this._getFootnoteData();

        this._handleFinishLoadingTrips();
      }
    }

    // Oslo 7600100
    // Chur 8509000
    // Bern Bahnhof 8576646
    // Europaplatz 8580939
    // Zürich 8503000
    // Paris 198
    // Milano 8301700

    /* eslint-disable max-len */
    // Brückfeldstrasse 40
    // "A=2@O=3012 Bern, Brückfeldstrasse 40@H=40@X=7434591@Y=46957527@U=103@L=990113774@B=1@p=1731322982@"

    // Bern, Manor, Marktgasse
    // "A=4@O=Bern, Manor, Marktgasse@X=7446637@Y=46948070@U=104@L=980073981@B=1@p=1731322823@"
    /* eslint-enable max-len */

  }

  public disconnectedCallback(): void {
    this._searchForm.removeEventListener(searchEvents.demandsTimetableRequest, this._handleSearchFormSearchRequest);
    this._searchForm.removeEventListener(searchEvents.demandsJourneySummaryUpdate, this._handleSummaryUpdate);
    this._resultsList.removeEventListener(resultsEvents.prevNextClicked, this._handlePrevNextClicked);
    this._resultsList.removeEventListener(rowEvents.timeTableRowButtonChanged, this._handleRowButtonChanged);
    this._resultsList.removeEventListener(linkEvents.clicked, this._trackBuyButtonClick);
  }

  public render(): HTMLDivElement {

    const tripsByDay = splitTripsByDate(this._trips, this.language);
    const tripsByDayKeys = Object.keys(tripsByDay);

    // EXCEPTION: if we search for 20.06.2025, but the first result available
    // is actually only on 21.06.2025, we need to show the day change.
    // -> check if the first result in the list is same as search date
    let forceDayChange = false;

    if (!(this._isLoadingTrips || this._isLoadingPrevNext) && tripsByDayKeys.length > 0) {
      const firstTrips = tripsByDay[tripsByDayKeys[0]];

      if (firstTrips.length > 0 && this._lastTripsRequest) {
        const [firstTrip] = firstTrips;
        const firstTripDate = formatDateForDatepicker(new Date(firstTrip.date));
        const searchDate = formatDateForDatepicker(this._lastTripsRequest.date);

        if (firstTripDate !== searchDate) {
          forceDayChange = true;
        }
      }

    }

    return (
      <div class='timetable'>
        <paf-search-form
          journeyServiceApiUrl={this.journeyServiceApiUrl}
          smapiUrl={this.smapiUrl}
          mock={this.mock}
          language={this.language}
          searchButtonLink={this.searchButtonLink}
          class='form'
          config={JSON.stringify(this._config)}
          data-testid='search-form'
          loading={this._isLoadingTrips}
          ref={(elem) => {
            this._searchForm = elem;
          }}
        />

        {this._summary &&
          <paf-journey-summary
            {...this._summary}
            class='summary'
            loading={this._isLoadingTrips && !this._isLoadingPrevNext}
            data-testid='journey-summary'
            onPaf-journey-summary_change={() => {
              this._searchForm.dispatchEvent(new CustomEvent('demand-focus'));
            }}
          />
        }
        <paf-result-timetable-list
          class='result-list'
          previousButtonText={this._config.results.earlierConnections[this.language]}
          nextButtonText={this._config.results.laterConnections[this.language]}
          buttonsHidden={(this._trips === undefined) || !(this._trips && this._trips.length)}
          buttonsDisabled={this._trips && this._isLoadingTrips}
          data-testid='result-list'
          ref={(elem) => {
            this._resultsList = elem;
          }}
          visible={this._trips !== undefined}
        >
          {(this._trips && this._trips.length === 0) &&
            <paf-no-results
              headingText={this._config.results.noResults.headingText[this.language]}
              headingLevel={this._config.results.noResults.headingLevel}
              description={this._config.results.noResults.description[this.language]}
            />
          }
          {this._trips && tripsByDayKeys
            .map((tripKey, dayChangeIndexes) => {

              const tripsOfDay = tripsByDay[tripKey];
              const output: (HTMLPafResultTimetableRowElement | HTMLPafResultDayChangeElement)[] = [];

              let showDayChange = false;

              if (!(this._isLoadingTrips || this._isLoadingPrevNext)) {

                // only show day change if it is not first day change index
                showDayChange = (dayChangeIndexes > 0 || tripsByDayKeys.length > 1);
              }

              if (forceDayChange) {
                showDayChange = true;
              }

              tripsOfDay.forEach((trip) => {
                output.push(this._renderTimetableRow(trip));
              });

              return (
                <Fragment>
                  <paf-result-day-change
                    headingText={tripKey}
                    headingLevel={3}
                    visuallyHidden={!showDayChange}
                  />
                  <ol
                    class='day-results'
                    aria-label={tripKey}
                    role='list'
                  >
                    {output}
                  </ol>
                </Fragment>
              );
            })}
        </paf-result-timetable-list>
      </div>
    );
  }
}
